export type TelemetrySeverity = 'info' | 'warning' | 'critical';
export type TelemetryEventData = Record<string, unknown>;

export interface TelemetryStartConfig {
    telemetryUrl: string;
    sessionId: string;
    deviceId: string;
    assessmentUuid: string;
    userUuid: string;
    swInstanceId: string;
    schoolCode?: number | null;
    /**
     * 'school' or 'specimen' in practice. Lets specimen rows be excluded with one
     * predicate. The CMS domain is wider (Publication.PACKAGE_TYPE_CHOICES also has
     * 'examiner' and 'media'), so treat anything else as unclassified.
     */
    packageType?: string;
    /** Frontend build number, so a row can be tied to the release that produced it. */
    appVersion?: string;
}

export interface ITelemetryBeaconService {
    start(config: TelemetryStartConfig): void;
    record(eventType: string, severity: TelemetrySeverity, data?: TelemetryEventData): void;
    setSwInstanceId(swInstanceId: string): void;
    setCandidateCode(candidateCode: string): void;
    forgetCandidate(): void;
    incrementSaveCount(): void;
    incrementSaveErrorCount(): void;
}

interface TelemetrySessionContext {
    sessionId: string;
    deviceId: string;
    assessmentUuid: string;
    userUuid: string;
    swInstanceId: string;
    schoolCode: number | null;
    candidateCode: string;
    packageType: string;
    appVersion: string;
}

interface TelemetryEvent {
    eventType: string;
    timestamp: number;
    sessionId: string;
    deviceId: string;
    assessmentUuid: string;
    userUuid: string;
    swInstanceId: string;
    schoolCode: number | null;
    candidateCode: string;
    packageType: string;
    appVersion: string;
    severity: TelemetrySeverity;
    data: TelemetryEventData;
}

interface StartTelemetryMessage {
    type: 'START_TELEMETRY';
    config: {
        telemetryUrl: string;
        uploadIntervalMs: number;
        sessionId: string;
        deviceId: string;
        assessmentUuid: string;
        userUuid: string;
        swInstanceId: string;
        schoolCode: number | null;
        candidateCode: string;
        packageType: string;
        appVersion: string;
    };
}

interface TelemetryEventMessage {
    type: 'TELEMETRY_EVENT';
    payload: TelemetryEvent;
}

/**
 * Patches fields onto an already-started session context. Needed because
 * `candidateNo` only becomes known when the user file loads, which is after
 * start().
 */
interface UpdateTelemetryContextMessage {
    type: 'UPDATE_TELEMETRY_CONTEXT';
    context: { candidateCode?: string };
}

type TelemetryWorkerMessage = StartTelemetryMessage | TelemetryEventMessage | UpdateTelemetryContextMessage;
type BrowserNavigator = Navigator & {
    serviceWorker?: ServiceWorkerContainer;
    sendBeacon?: (url: string, data?: BodyInit | null) => boolean;
};

type PerformanceWithMemory = Performance & { memory?: { usedJSHeapSize: number } };

/**
 * TelemetryBeaconService — Frontend counterpart of the SW TelemetryCollector.
 *
 * Responsibilities:
 * 1. Routes frontend telemetry events to the SW via postMessage
 * 2. Falls back to navigator.sendBeacon() when SW controller is unavailable
 * 3. Sends session.end via sendBeacon() on page unload (more reliable than SW route)
 * 4. Starts the SW TelemetryCollector with session context once assessment is loaded
 */
export default class TelemetryBeaconService implements ITelemetryBeaconService {
    private static readonly LOG_PREFIX = '[TelemetryBeacon]';
    private static readonly MAX_PENDING_EVENTS = 50;

    private sessionContext: TelemetrySessionContext | null = null;
    private telemetryUrl = '';
    private started = false;
    private cachedDeviceId = '';
    private lastOnlineTimestamp = Date.now();
    private offlineStartTimestamp: number | null = null;
    private saveCount = 0;
    private saveErrorCount = 0;
    private heartbeatIntervalId: number | null = null;
    private readonly HEARTBEAT_INTERVAL_MS = 300000;
    private pendingEvents: Array<{ eventType: string; severity: TelemetrySeverity; data: TelemetryEventData; timestamp: number }> = [];
    /**
     * Last known candidateNo, retained across start() calls.
     *
     * Deliberately NOT cleared by start(). The pre-login start() creates a context,
     * so a USER_FILE_LOADED during the login start()'s deferred school-code lookup
     * lands on the pre-login context; if this were a "pending only" field it would be
     * empty by the time the login context is built, and the real session would ship
     * with no candidate code at all.
     */
    private knownCandidateCode = '';

    public start(config: TelemetryStartConfig): void {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('online', this.onOnline);

        this.cachedDeviceId = config.deviceId || this.getDeviceId();
        this.sessionContext = {
            sessionId: config.sessionId || '',
            deviceId: this.cachedDeviceId,
            assessmentUuid: config.assessmentUuid || '',
            userUuid: config.userUuid || '',
            swInstanceId: config.swInstanceId || '',
            schoolCode: config.schoolCode != null ? config.schoolCode : null,
            // A candidateNo already known (whether it arrived before any start() or
            // during an earlier pre-login session) is applied here rather than via a
            // follow-up UPDATE_TELEMETRY_CONTEXT, so it ships inside the single
            // START_TELEMETRY message below.
            candidateCode: this.knownCandidateCode || '',
            packageType: config.packageType || '',
            appVersion: config.appVersion || TelemetryBeaconService.getAppVersion(),
        };
        this.telemetryUrl = config.telemetryUrl || '';
        this.started = true;
        this.saveCount = 0;
        this.saveErrorCount = 0;

        this.postToSW({
            type: 'START_TELEMETRY',
            config: {
                telemetryUrl: this.telemetryUrl,
                uploadIntervalMs: 60000,
                sessionId: this.sessionContext.sessionId,
                deviceId: this.sessionContext.deviceId,
                assessmentUuid: this.sessionContext.assessmentUuid,
                userUuid: this.sessionContext.userUuid,
                swInstanceId: this.sessionContext.swInstanceId,
                schoolCode: this.sessionContext.schoolCode,
                candidateCode: this.sessionContext.candidateCode,
                packageType: this.sessionContext.packageType,
                appVersion: this.sessionContext.appVersion,
            }
        });

        window.addEventListener('beforeunload', this.onBeforeUnload);
        window.addEventListener('offline', this.onOffline);
        window.addEventListener('online', this.onOnline);

        if (this.heartbeatIntervalId !== null) {
            window.clearInterval(this.heartbeatIntervalId);
        }
        this.heartbeatIntervalId = window.setInterval(this.sendHeartbeat, this.HEARTBEAT_INTERVAL_MS);

        // Flush events queued before start, hydrated with session context
        if (this.pendingEvents.length > 0) {
            const hydratedEvents = this.pendingEvents.map(({ eventType, severity, data, timestamp }) => ({
                ...this.buildEvent(eventType, severity, data),
                timestamp,
            }));
            this.sendBeacon(hydratedEvents);
            this.pendingEvents = [];
        }

        console.debug(`${TelemetryBeaconService.LOG_PREFIX} Started`, this.sessionContext);
    }

    public record(eventType: string, severity: TelemetrySeverity, data: TelemetryEventData = {}): void {
        if (!this.started) {
            if (this.pendingEvents.length < TelemetryBeaconService.MAX_PENDING_EVENTS) {
                this.pendingEvents.push({ eventType, severity, data, timestamp: Date.now() });
            }
            return;
        }

        // Telemetry is for operational metrics only — Sentry reporting should
        // be done explicitly at the call site where full error context is available.
        const event = this.buildEvent(eventType, severity, data);

        const browserNavigator = window.navigator as BrowserNavigator;
        const controller = browserNavigator.serviceWorker?.controller;
        if (controller) {
            try {
                controller.postMessage({
                    type: 'TELEMETRY_EVENT',
                    payload: event
                } satisfies TelemetryEventMessage);
                return;
            } catch (error) {
                console.warn(`${TelemetryBeaconService.LOG_PREFIX} Failed to postMessage to SW:`, error);
                // Fall through to sendBeacon
            }
        }

        this.sendBeacon([event]);
    }

    public setSwInstanceId(swInstanceId: string): void {
        if (this.sessionContext) {
            this.sessionContext.swInstanceId = swInstanceId;
        }
    }

    /**
     * Forgets the candidate-specific identity at the end of a sitting.
     *
     * knownCandidateCode deliberately survives start() so the pre-login session can
     * hand it to the login session, which means it would otherwise also survive into
     * the NEXT candidate's sitting: clearExamSessionAndCleanup only reloads the page
     * when a deferred SW update is pending, so returning to /pwa-start can reuse the
     * same Angular singleton. The next candidate's pre-login events would then carry
     * the previous candidate's code, which is the same cross-candidate contamination
     * the SW-side context expiry prevents.
     */
    public forgetCandidate(): void {
        this.knownCandidateCode = '';
        if (this.sessionContext) {
            this.sessionContext.candidateCode = '';
        }
        // The SW keeps its own copy, and recordFromFrontend treats an empty page value
        // as "absent" and falls back to that copy, so clearing only the page side
        // would let the finished sitting's code restamp the next candidate's early
        // pre-login events. Forward the clear explicitly.
        this.postToSW({
            type: 'UPDATE_TELEMETRY_CONTEXT',
            context: { candidateCode: '' },
        });
    }

    /**
     * Supplies the authoritative `candidateNo` once the user file has been decrypted.
     *
     * Telemetry starts at login, but `candidateNo` only exists after the /user/<uuid>
     * route loads the user file, so it is patched in afterwards and forwarded to the
     * SW collector. Events emitted in that window carry an empty candidate code, which
     * `session.start`'s candidateCodeSource field records explicitly.
     *
     * Retained in knownCandidateCode regardless of whether a context exists yet, so
     * neither ordering loses it: start() may be deferred behind the managed-config
     * lookup (whose retry ladder can outlast the user file fetch), and a pre-login
     * start() may already have created a context that the login start() replaces.
     */
    public setCandidateCode(candidateCode: string): void {
        const code = candidateCode || '';
        if (!code) {
            console.warn(`${TelemetryBeaconService.LOG_PREFIX} Empty candidateNo, candidate_code will stay blank`);
            return;
        }
        // Recorded first, so a later start() picks it up even when this call also
        // applies it to a context that start() is about to replace.
        this.knownCandidateCode = code;

        if (!this.sessionContext || this.sessionContext.candidateCode === code) {
            return;
        }
        this.sessionContext.candidateCode = code;
        this.postToSW({
            type: 'UPDATE_TELEMETRY_CONTEXT',
            context: { candidateCode: code },
        });
    }

    public incrementSaveCount(): void {
        this.saveCount++;
    }

    public incrementSaveErrorCount(): void {
        this.saveErrorCount++;
    }

    private readonly onOffline = (): void => {
        this.offlineStartTimestamp = Date.now();
        this.record('network.offline', 'warning', {
            lastOnlineTs: this.lastOnlineTimestamp,
        });
    };

    private readonly onOnline = (): void => {
        const offlineDurationMs = this.offlineStartTimestamp ? (Date.now() - this.offlineStartTimestamp) : 0;
        this.record('network.online', 'info', {
            offlineDurationMs,
        });
        this.lastOnlineTimestamp = Date.now();
        this.offlineStartTimestamp = null;
    };

    private readonly onBeforeUnload = (): void => {
        if (this.heartbeatIntervalId !== null) {
            window.clearInterval(this.heartbeatIntervalId);
            this.heartbeatIntervalId = null;
        }
        const endEvent = this.buildEvent('session.end', 'info', {
            totalSaves: this.saveCount,
            totalSaveErrors: this.saveErrorCount,
            // Which screen the session ended on separates a clean finish at /taskdone/
            // from a candidate who vanished mid-sheet.
            route: this.getRoute(),
        });
        this.sendBeacon([endEvent]);
    };

    private readonly sendHeartbeat = (): void => {
        this.record('session.heartbeat', 'info', {
            saveCount: this.saveCount,
            saveErrorCount: this.saveErrorCount,
            online: navigator.onLine,
            memoryMB: this.getMemoryMB(),
            visibilityState: document.visibilityState || 'unknown',
            // Where the candidate actually is. Without this, a 5-minutely heartbeat
            // tells you a session is alive but not whether it is stuck on one screen.
            route: this.getRoute(),
            hasSwController: this.hasServiceWorkerController(),
        });
    };

    /** Current in-app route from the hash, with any ids left intact. */
    private getRoute(): string {
        try {
            return (window.location.hash || '').replace(/^#/, '').substring(0, 120);
        } catch (error) {
            return '';
        }
    }

    private buildEvent(eventType: string, severity: TelemetrySeverity, data: TelemetryEventData): TelemetryEvent {
        return {
            eventType,
            timestamp: Date.now(),
            sessionId: this.sessionContext?.sessionId || '',
            deviceId: this.sessionContext?.deviceId || this.cachedDeviceId || '',
            assessmentUuid: this.sessionContext?.assessmentUuid || '',
            userUuid: this.sessionContext?.userUuid || '',
            swInstanceId: this.sessionContext?.swInstanceId || '',
            schoolCode: this.sessionContext?.schoolCode != null ? this.sessionContext.schoolCode : null,
            candidateCode: this.sessionContext?.candidateCode || '',
            packageType: this.sessionContext?.packageType || '',
            // Fall back to the raw source so events recorded before start() (queued in
            // pendingEvents) and after a page reload still carry the build number.
            appVersion: this.sessionContext?.appVersion || TelemetryBeaconService.getAppVersion(),
            severity,
            data,
        };
    }

    private static getAppVersion(): string {
        // @ts-ignore - __BUILD_NO__ is injected by webpack DefinePlugin; undefined under Jest
        return typeof __BUILD_NO__ !== 'undefined' ? String(__BUILD_NO__) : '';
    }

    private hasServiceWorkerController(): boolean {
        const browserNavigator = window.navigator as BrowserNavigator;
        return !!browserNavigator.serviceWorker?.controller;
    }

    private postToSW(message: TelemetryWorkerMessage): void {
        try {
            if (this.hasServiceWorkerController()) {
                const browserNavigator = window.navigator as BrowserNavigator;
                browserNavigator.serviceWorker!.controller!.postMessage(message);
                return;
            }
            // Previously a silent no-op. Losing START_TELEMETRY here is what leaves
            // the SW collector running on a stale restored context, so it needs to be
            // visible rather than inferred from blank columns later.
            console.warn(
                `${TelemetryBeaconService.LOG_PREFIX} No SW controller, dropped ${message.type}`,
            );
        } catch (error) {
            console.warn(`${TelemetryBeaconService.LOG_PREFIX} Failed to postMessage to SW:`, error);
        }
    }

    private sendBeacon(events: TelemetryEvent[]): void {
        if (!this.telemetryUrl) {
            console.warn(`${TelemetryBeaconService.LOG_PREFIX} No telemetryUrl, dropped ${events.length} event(s)`);
            return;
        }

        try {
            const blob = new Blob([JSON.stringify(events)], { type: 'application/json' });
            const browserNavigator = window.navigator as BrowserNavigator;
            // sendBeacon returns false rather than throwing when the UA refuses the
            // payload, typically over its ~64KB queue limit, so the try/catch alone
            // let session.end and the pending-event flush vanish without a trace.
            const queued = browserNavigator.sendBeacon?.(this.telemetryUrl, blob);
            if (queued === false) {
                console.warn(
                    `${TelemetryBeaconService.LOG_PREFIX} sendBeacon refused ${events.length} event(s), retrying via fetch`,
                );
                this.sendViaFetch(blob);
            }
        } catch (error) {
            console.warn(`${TelemetryBeaconService.LOG_PREFIX} sendBeacon failed:`, error);
        }
    }

    /**
     * Fallback for a refused beacon. `keepalive` lets the request outlive the page,
     * which is what makes it usable from the beforeunload path.
     */
    private sendViaFetch(blob: Blob): void {
        try {
            void fetch(this.telemetryUrl, {
                method: 'POST',
                body: blob,
                keepalive: true,
            }).catch(() => { /* best-effort: telemetry must not break unload */ });
        } catch (error) {
            console.warn(`${TelemetryBeaconService.LOG_PREFIX} fetch fallback failed:`, error);
        }
    }

    private getMemoryMB(): number {
        try {
            const performanceWithMemory = performance as PerformanceWithMemory;
            if (performanceWithMemory.memory) {
                return Math.round(performanceWithMemory.memory.usedJSHeapSize / 1048576);
            }
        } catch (error) {
            // memory metrics are best-effort
        }
        return -1;
    }

    private getDeviceId(): string {
        try {
            const match = document.cookie.match(/ib_device_id=([^;]+)/);
            return match ? match[1] : '';
        } catch (error) {
            return '';
        }
    }
}
